import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reports, type GoodsReservedRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

function endOfToday(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 0);
  const tz = -d.getTimezoneOffset();
  const sign = tz >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(tz / 60)}:${pad(tz % 60)}`;
}

const fmtQty = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });

export default function GoodsReservedPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [onDate, setOnDate] = useState<string>(() => endOfToday());

  if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);

  const reportQ = useQuery({
    queryKey: ['goods-reserved', orgId, onDate],
    queryFn: () => reports.goodsReserved({
      organization_id: orgId,
      on_date: onDate,
    }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<GoodsReservedRow>[]>(() => [
    { key: 'warehouse_code', header: 'Склад', width: 100, sortValue: (r) => r.warehouse_code ?? '', cell: (r) => r.warehouse_code },
    { key: 'nomenclature_code', header: 'Код', width: 120, sortValue: (r) => r.nomenclature_code ?? '', cell: (r) => r.nomenclature_code },
    { key: 'nomenclature_name', header: 'Номенклатура', sortValue: (r) => r.nomenclature_name ?? '', cell: (r) => r.nomenclature_name },
    {
      key: 'quantity', header: 'Зарезервовано', width: 150, align: 'right',
      sortValue: (r) => Number(r.quantity),
      cell: (r) => <span style={{ fontWeight: 600 }}>{fmtQty(r.quantity)}</span>,
    },
    { key: 'customer_order_number', header: '№ замовлення', width: 150, sortValue: (r) => r.customer_order_number ?? '', cell: (r) => r.customer_order_number },
    {
      key: 'customer_order_date', header: 'Дата замовлення', width: 150,
      sortValue: (r) => r.customer_order_date ?? '',
      cell: (r) => r.customer_order_date ? new Date(r.customer_order_date).toLocaleDateString('uk-UA') : '',
    },
    { key: 'contractor_name', header: 'Покупець', sortValue: (r) => r.contractor_name ?? '', cell: (r) => r.contractor_name },
  ], []);

  const totalReserved = (reportQ.data?.rows ?? []).reduce(
    (s, r) => s + Number(r.quantity), 0,
  );

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Товари в резерві</h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>На дату</label>
          <DatePopover
            withTime
            value={onDate.slice(0, 16)}
            onChange={(v) => setOnDate(v + ':00+02:00')}
          />
        </div>
      </div>

      {reportQ.data && (
        <div style={{
          padding: '0.5rem 0.75rem', background: '#f8fafc', borderRadius: 6,
          marginBottom: '0.75rem', fontSize: '0.9rem', color: '#475569',
        }}>
          Записів: <strong>{reportQ.data.rows.length}</strong> &nbsp; · &nbsp;
          Усього зарезервовано (одиниць): <strong>{fmtQty(String(totalReserved))}</strong>
        </div>
      )}

      <DataTable<GoodsReservedRow>
        rows={reportQ.data?.rows ?? []}
        columns={cols}
        rowKey={(r) => `${r.warehouse_id}|${r.nomenclature_id}|${r.customer_order_id}`}
        loading={reportQ.isLoading}
        emptyText="Немає даних."
      />

      <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
        Список показує тільки активні резерви — кількість, що ще не відвантажена
        за відповідним замовленням покупця. При проведенні реалізації зі ссилкою
        на замовлення резерв автоматично знімається у відвантаженій кількості.
      </p>
    </>
  );
}
